
import { useEffect, useId, useState } from 'react';
import Link from '@/components/compat/Link';
import { useRouter } from '@/lib/navigation';
import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import { X } from 'lucide-react';
import {
  type PowerMentionData,
  formatSlugAsName,
  getPowerMentionInfo,
} from '@/lib/power-mention-data';
import PowerMentionPreview from '@/components/help-center/powers/PowerMentionPreview';
import { useCoarsePointer } from '@/hooks/use-coarse-pointer';
import { cn } from '@/lib/utils';

const OPEN_DELAY_MS = 180;
const CLOSE_DELAY_MS = 150;

export const powerMentionLinkClass = cn(
  'font-medium text-sky-600 decoration-sky-500/40 underline-offset-2',
  'transition-[color,text-decoration-color] duration-200',
  'hover:text-sky-500 hover:underline',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50 focus-visible:ring-offset-1',
  'cursor-pointer'
);

interface PowerMentionProps {
  slug: string;
  children?: React.ReactNode;
  className?: string;
}

function usePowerMentionLazyData(slug: string, enabled: boolean) {
  const [data, setData] = useState<PowerMentionData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    getPowerMentionInfo(slug)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, enabled]);

  return { data, loading };
}

export default function PowerMention({ slug, children, className }: PowerMentionProps) {
  const key = slug.trim().toLowerCase();
  const label = children ?? formatSlugAsName(key);
  const isTouch = useCoarsePointer();
  const router = useRouter();
  const previewId = useId();

  const [cardOpen, setCardOpen] = useState(false);
  const [prefetch, setPrefetch] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [mobilePrimed, setMobilePrimed] = useState(false);

  const previewEnabled = isTouch ? sheetOpen : prefetch || cardOpen;
  const { data, loading } = usePowerMentionLazyData(key, previewEnabled);

  useEffect(() => {
    if (!sheetOpen) setMobilePrimed(false);
  }, [sheetOpen]);

  useEffect(() => {
    if (!sheetOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSheetOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [sheetOpen]);

  const handleMobileTap = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    if (mobilePrimed && sheetOpen) {
      router.push(`/powers/${key}`);
      setSheetOpen(false);
      return;
    }
    setMobilePrimed(true);
    setSheetOpen(true);
  };

  if (isTouch) {
    return (
      <>
        <button
          type="button"
          className={cn(powerMentionLinkClass, 'inline bg-transparent p-0 text-left', className)}
          onClick={handleMobileTap}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleMobileTap(e);
            }
          }}
          aria-expanded={sheetOpen}
          aria-haspopup="dialog"
          aria-controls={sheetOpen ? previewId : undefined}
        >
          {label}
        </button>

        {sheetOpen ? (
          <div
            id={previewId}
            role="dialog"
            aria-modal="false"
            aria-label={`Preview do power ${typeof label === 'string' ? label : key}`}
            className={cn(
              'fixed inset-x-0 bottom-0 z-50 px-5 pb-8 pt-4',
              'rounded-t-3xl border-t border-sky-500/20 bg-[#030023]/98',
              'shadow-[0_-8px_40px_rgba(0,0,0,0.55),0_0_24px_rgba(56,189,248,0.1)]',
              'backdrop-blur-xl animate-in slide-in-from-bottom duration-300'
            )}
          >
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/15" aria-hidden />
            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              className="absolute right-4 top-4 rounded-sm p-1 text-blue-100/60 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50"
              aria-label="Fechar preview"
            >
              <X className="h-4 w-4" />
            </button>
            <PowerMentionPreview
              data={data}
              loading={loading}
              variant="sheet"
              onNavigate={() => setSheetOpen(false)}
            />
            {mobilePrimed ? (
              <p className="mt-3 text-center font-inter text-[11px] text-blue-100/45">
                Toque novamente no nome para abrir a página do power.
              </p>
            ) : null}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <HoverCardPrimitive.Root
      openDelay={OPEN_DELAY_MS}
      closeDelay={CLOSE_DELAY_MS}
      onOpenChange={setCardOpen}
    >
      <HoverCardPrimitive.Trigger asChild>
        <Link
          href={`/powers/${key}`}
          className={cn(powerMentionLinkClass, className)}
          aria-describedby={cardOpen ? previewId : undefined}
          onMouseEnter={() => setPrefetch(true)}
          onFocus={() => setPrefetch(true)}
        >
          {label}
        </Link>
      </HoverCardPrimitive.Trigger>

      <HoverCardPrimitive.Portal>
        <HoverCardPrimitive.Content
          id={previewId}
          role="tooltip"
          side="top"
          align="center"
          sideOffset={10}
          className={cn(
            'z-50 w-[min(100vw-2rem,330px)] overflow-hidden rounded-2xl p-0 outline-none',
            'border border-sky-500/25 bg-[#030023]/95 backdrop-blur-xl',
            'shadow-[0_8px_40px_rgba(0,0,0,0.55),0_0_24px_rgba(56,189,248,0.14),inset_0_1px_0_rgba(255,255,255,0.06)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2'
          )}
        >
          <PowerMentionPreview data={data} loading={loading} variant="popover" />
          <HoverCardPrimitive.Arrow
            className="fill-[#030023]/95"
            width={14}
            height={7}
          />
        </HoverCardPrimitive.Content>
      </HoverCardPrimitive.Portal>
    </HoverCardPrimitive.Root>
  );
}
